import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
import { UserIcon, EnvelopeIcon, LockClosedIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

type RegisterFormInputs = {
  name: string;
  email: string;
  password: string;
  phone: string;
  confirmPassword: string;
};

export default function CustomerRegister() {
  const { register: signup } = useAuth();
  const navigate = useNavigate();
  const [registrationStep, setRegistrationStep] = useState<'idle' | 'creating-account' | 'setting-up-profile' | 'completing'>('idle');
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormInputs>({
    mode: 'onBlur',
  });

  const onSubmit: SubmitHandler<RegisterFormInputs> = async ({ name, email, password, phone }) => {
    try {
      console.log('🚀 Starting registration process...');
      setRegistrationStep('creating-account');
      
      await signup({
        email,
        password,
        name,
        phone,
        role: 'customer',
      });
      
      setRegistrationStep('completing');
      console.log('✅ Registration successful, navigating to dashboard...');
      
      // Small delay to show completion step
      setTimeout(() => {
        navigate('/portal/dashboard');
        toast.success('Account created successfully! Welcome!');
      }, 500);
      
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      setRegistrationStep('idle');
      toast.error(error.message || 'Failed to create account.');
    }
  };

  const getStepMessage = () => {
    switch (registrationStep) {
      case 'creating-account':
        return 'Creating your account...';
      case 'setting-up-profile':
        return 'Setting up your profile...';
      case 'completing':
        return 'Completing registration...';
      default:
        return '';
    }
  };

  if (registrationStep !== 'idle') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <LoadingSpinner />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            {getStepMessage()}
          </h2>
          <p className="mt-2 text-gray-600">
            Please wait while we set up your account...
          </p>
          {registrationStep === 'creating-account' && (
            <div className="mt-4">
              <div className="w-64 bg-gray-200 rounded-full h-2 mx-auto">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-1000" style={{ width: '60%' }}></div>
              </div>
            </div>
          )}
          {registrationStep === 'setting-up-profile' && (
            <div className="mt-4">
              <div className="w-64 bg-gray-200 rounded-full h-2 mx-auto">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-1000" style={{ width: '80%' }}></div>
              </div>
            </div>
          )}
          {registrationStep === 'completing' && (
            <div className="mt-4">
              <div className="w-64 bg-gray-200 rounded-full h-2 mx-auto">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-1000" style={{ width: '100%' }}></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your Customer Account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              Sign in here
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="name" className="sr-only">Full Name</label>
              <div className="relative">
                 <UserIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
                 <input
                  {...register('name', { required: 'Full name is required' })}
                  type="text"
                  id="name"
                  className="input pl-10"
                  placeholder="Full Name"
                />
              </div>
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
            </div>
            <div>
              <label htmlFor="email" className="sr-only">Email address</label>
              <div className="relative">
                <EnvelopeIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
                <input
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  id="email"
                  className="input pl-10"
                  placeholder="Email address"
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
            </div>
            <div>
              <label htmlFor="phone" className="sr-only">Phone number</label>
              <div className="relative">
                <PhoneIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
                <input
                  {...register('phone', { required: 'Phone number is required' })}
                  type="tel"
                  id="phone"
                  className="input pl-10"
                  placeholder="Phone number"
                />
              </div>
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <div className="relative">
                <LockClosedIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
                <input
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  type="password"
                  id="password"
                  className="input pl-10"
                  placeholder="Password"
                />
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="sr-only">Confirm Password</label>
              <div className="relative">
                <LockClosedIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
                <input
                  {...register('confirmPassword', { 
                    required: 'Please confirm your password',
                    validate: (value, formValues) => value === formValues.password || 'Passwords do not match'
                  })}
                  type="password"
                  id="confirmPassword"
                  className="input pl-10"
                  placeholder="Confirm Password"
                />
              </div>
              {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner />
                  <span className="ml-2">Creating Account...</span>
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 